import { useCallback, useEffect, useRef, useState } from "react";

import * as api from "../../lib/api.js";
import { t } from "../contract/strings";
import { ScreenHeading, Skeleton, SkeletonGate, SkeletonLine, StatusLine, cx } from "../ui";
import {
  BoltIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  SparkleIcon,
  SpeakerNotesIcon,
  TimerIcon,
  WarningIcon,
} from "../ui/icons";
import { fmtDateTime } from "./past-sessions";
import { Screen } from "./screen";
import { useScreenStatus } from "./use-screen-status";
import {
  transactionAmountParts,
  transactionIcon,
  transactionTitle,
  type TransactionIconName,
  type Wallet,
} from "./wallet";

type Transaction = Wallet["transactions"][number];

/** Each row icon's glyph. */
const ROW_ICONS: Record<TransactionIconName, typeof ClockIcon> = {
  "speaker-notes": SpeakerNotesIcon,
  sparkle: SparkleIcon,
  bolt: BoltIcon,
  timer: TimerIcon,
  warning: WarningIcon,
  "check-circle": CheckCircleIcon,
  calendar: CalendarIcon,
  clock: ClockIcon,
};

/**
 * Rows per page: Recent Meetings' page size. With the server's `X-Total-Count` it is also
 * how "is there more" is answered -- no extra row and no page numbers.
 */
export const PAGE_SIZE = 8;

/** One page of the ledger, as `walletTransactions` resolves it. */
type TransactionsPage = Transaction[] & { total?: number | null };

/**
 * A later page added to the rows held. The offset is how many rows are held, so a movement
 * recorded since the last page shifts the boundary by one and that page begins with a row
 * already shown: it is dropped by id rather than drawn twice.
 */
export function appendPage(held: Transaction[], fetched: Transaction[]): Transaction[] {
  const seen = new Set(held.map((tx) => tx.id));
  return [...held, ...fetched.filter((tx) => !seen.has(tx.id))];
}

/**
 * Whether there is more to ask for: the server's count when it sent one, and otherwise a
 * full last page.
 */
export function hasMoreRows(held: number, total: number | null, lastPage: number): boolean {
  return total !== null ? held < total : lastPage === PAGE_SIZE;
}

/**
 * Credits history: every movement of this account's credits, newest first, loaded as the
 * list is scrolled, exactly as Recent Meetings is (`sessions.tsx`). Account shows the latest
 * few rows and links here for the rest. The guard is `signed-in`: the ledger belongs to the
 * account.
 *
 * There is no pager and no page state. The list asks for the next page when its end marker
 * comes within 96px of the pane's bottom, so the offset is simply how many rows are already
 * held. The pane is the only thing that scrolls: the app header and the screen's heading
 * are fixed (`body[data-screen="credits_history"]` in `app.css`).
 */
export function CreditsHistory() {
  const [status, say] = useScreenStatus();

  const [rows, setRows] = useState<Transaction[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const paneRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  /** One fetch at a time: an intersection that arrives mid-flight is dropped, not queued. */
  const inFlight = useRef(false);
  /** The last later page failed, so its line is on the status line until one lands. */
  const failedMore = useRef(false);

  const empty = rows !== null && rows.length === 0;

  useEffect(() => {
    let alive = true;
    api.walletTransactions({ limit: PAGE_SIZE, offset: 0 }).then(
      (fetched: TransactionsPage) => {
        if (!alive) return;
        const first = fetched.slice(0, PAGE_SIZE);
        setRows(first);
        setHasMore(hasMoreRows(first.length, typeof fetched.total === "number" ? fetched.total : null, fetched.length));
        setLoading(false);
      },
      (err: Error) => {
        if (!alive) return;
        setRows(null);
        setHasMore(false);
        setLoading(false);
        console.warn("walletTransactions failed", err);
        say(t("billing.load_error"), "err");
      }
    );
    return () => {
      alive = false;
    };
  }, [say]);

  /** One page, appended. A failure keeps the rows already on screen. */
  const loadMore = useCallback(() => {
    const held = rows ?? [];
    if (inFlight.current || !hasMore) return;
    inFlight.current = true;
    setLoadingMore(true);
    api.walletTransactions({ limit: PAGE_SIZE, offset: held.length }).then(
      (fetched: TransactionsPage) => {
        const next = appendPage(held, fetched);
        setRows(next);
        setHasMore(hasMoreRows(next.length, typeof fetched.total === "number" ? fetched.total : null, fetched.length));
        setLoadingMore(false);
        inFlight.current = false;
        // A page that lands after a failed one clears the failure's line.
        if (failedMore.current) say("", "");
        failedMore.current = false;
      },
      (err: Error) => {
        // The rows above are still good, so this is a line at the screen's foot, not a
        // page-wide failure. Scrolling again retries.
        setLoadingMore(false);
        inFlight.current = false;
        failedMore.current = true;
        console.warn("walletTransactions more failed", err);
        say(t("billing.load_error"), "err");
      }
    );
  }, [rows, hasMore, say]);

  useEffect(() => {
    if (!hasMore || typeof IntersectionObserver === "undefined") return;
    const pane = paneRef.current;
    const sentinel = sentinelRef.current;
    if (!pane || !sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      // Rooted on the pane, not the viewport: this list is the only thing that scrolls.
      { root: pane, rootMargin: "0px 0px 96px 0px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  return (
    <Screen name="credits_history">
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex-none">
          <ScreenHeading>{t("billing.history_title")}</ScreenHeading>
        </div>

        {/* The pane: the only thing that scrolls. It takes focus so a keyboard can scroll
            it, and it is named by the heading above it. `relative` makes it the containing
            block of the `sr-only` status below, which is absolutely positioned: without it
            that line escapes the pane's clip and scrolls the page. */}
        <div
          ref={paneRef}
          role="region"
          aria-label={t("billing.history_title")}
          tabIndex={0}
          className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain pb-4"
        >
          {/* The first page's load is a skeleton (#1043) of a full first page (#1049), as
              Recent Meetings'; a failure is the status line below. */}
          <SkeletonGate
            loading={rows === null && loading}
            label={t("billing.loading")}
            skeleton={
              <div className="grid gap-2 my-4">
                {Array.from({ length: PAGE_SIZE }, (_, i) => (
                  <TransactionRowSkeleton key={i} />
                ))}
              </div>
            }
          >
            {empty ? (
              <div className="bg-surface border border-line rounded-card p-pad my-4 text-center">
                <p className="m-0 text-lede">{t("billing.history_empty")}</p>
              </div>
            ) : null}

            <div className="grid gap-2 my-4">
              {(rows ?? []).map((tx) => (
                <TransactionRow key={tx.id} tx={tx} />
              ))}
            </div>

            {loadingMore ? (
              <div className="mb-4">
                <TransactionRowSkeleton />
              </div>
            ) : null}

            {/* The end marker, and the observer's only target. It exists while there is
                more, so the observer disconnects itself when the ledger runs out. There is
                no end-of-list line. */}
            {hasMore ? <div ref={sentinelRef} className="h-px" aria-hidden="true" /> : null}

            {/* One announcement for the wait, not one per scroll. */}
            <p className="sr-only" role="status">
              {loadingMore ? t("billing.loading") : ""}
            </p>
          </SkeletonGate>
        </div>

        <StatusLine kind={status.kind}>{status.message}</StatusLine>
      </div>
    </Screen>
  );
}

/**
 * `TransactionRow` while the history loads (#1043): the same box, the icon squircle, the
 * title and when, and the amount at the end, as static blocks.
 */
export function TransactionRowSkeleton() {
  return (
    <div className="min-w-0 flex w-full items-center gap-stack bg-surface border border-line rounded-control py-stack px-gutter">
      <Skeleton className="size-tile-md rounded-squircle flex-none" />
      <span className="min-w-0 flex-1">
        <SkeletonLine size="person" width="w-2/5" />
        <SkeletonLine size="note" width="w-3/5" />
      </span>
      <Skeleton className="w-tile-xs h-icon-sm rounded-pill flex-none" />
    </div>
  );
}

/**
 * One history row: its kind's icon in a quiet squircle, what it
 * was (wrapping rather than cut short), when, and the signed amount pinned to the end in the
 * instrument face. Rows do not open; a meeting's notes live in Past meetings. Account's
 * preview and Credits history share it.
 */
export function TransactionRow({ tx }: { tx: Transaction }) {
  const Icon = ROW_ICONS[transactionIcon(tx)];
  const added = tx.amount_credits >= 0;
  const [amountBefore, amountCredits, amountAfter] = transactionAmountParts(tx);
  return (
    <div className="min-w-0 flex w-full items-center gap-stack bg-surface border border-line rounded-control py-stack px-gutter text-left">
      <span className="size-tile-md rounded-squircle bg-surface-3 border border-line-strong text-accent flex items-center justify-center flex-none">
        <Icon className="size-icon" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block break-words font-title text-person font-semibold tracking-headline text-ink">
          {transactionTitle(tx)}
        </span>
        {/* When, to the minute, in the viewer's own zone and locale. */}
        <span className="block text-note text-ink-soft">
          {tx.type === "overage_writeoff"
            ? `${fmtDateTime(tx.created_at)} · ${t("billing.tx_writeoff_detail")}`
            : fmtDateTime(tx.created_at)}
        </span>
      </span>
      {/* The signed number 30% larger and bold, the unit after it as it was, on one
          baseline. */}
      <span
        className={cx(
          "flex-none whitespace-nowrap font-code text-count font-semibold uppercase tracking-label tabular-nums",
          added ? "text-ok" : "text-ink-soft"
        )}
      >
        <span className="text-[calc(var(--text-count)*1.3)] font-bold">
          {amountBefore}
          {amountCredits}
        </span>
        {amountAfter}
      </span>
    </div>
  );
}
